import { MapUtils } from '../../utility/map-utils.js';
import { VersionRange } from '../core/version-range.js';
import { isSameData } from '../dto/data-model-utils.js';
import { DataModel, DataModelBuilder, SerializationContext, SerializedData } from '../dto/data-model.js';
import { BusinessModuleRef } from './business-module-ref.js';

export class BusinessBrevoTemplate extends DataModel {
  static readonly TYPE_ID: string = '59f584e68fcd446fae53e6cdf6250c0b';
  static readonly SCHEMA_VERSION: number = 2;

  private constructor(
    public readonly businessRef: BusinessModuleRef,
    public readonly templateIdHu: number,
    public readonly templateIdRo: number,
    public readonly scheduledHour: number | null,
  ) {
    super();
  }

  get $typeId(): string {
    return BusinessBrevoTemplate.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.exactly(BusinessBrevoTemplate.SCHEMA_VERSION);
  }

  static create(args: {
    businessRef: BusinessModuleRef,
    templateIdHu: number,
    templateIdRo: number,
    scheduledHour: number | null,
  }): BusinessBrevoTemplate {
    return new BusinessBrevoTemplate(
        args.businessRef,
        args.templateIdHu,
        args.templateIdRo,
        args.scheduledHour
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): BusinessBrevoTemplate {
    return BusinessBrevoTemplate.create({
      businessRef: BusinessModuleRef.mustDecode(MapUtils.getString(map, 'businessRef')),
      templateIdHu: MapUtils.getInteger(map, 'templateIdHu'),
      templateIdRo: MapUtils.getInteger(map, 'templateIdRo'),
      scheduledHour: MapUtils.getIntegerOrNull(map, 'scheduledHour'),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'businessRef': this.businessRef.encode(),
      'templateIdHu': this.templateIdHu,
      'templateIdRo': this.templateIdRo,
      'scheduledHour': this.scheduledHour,
    };
  }

  clone(): this {
    return this.copyWith();
  }

  copyWith({
    businessRef = this.businessRef,
    templateIdHu = this.templateIdHu,
    templateIdRo = this.templateIdRo,
    scheduledHour = this.scheduledHour,
  }: {
    businessRef?: BusinessModuleRef,
    templateIdHu?: number,
    templateIdRo?: number,
    scheduledHour?: number | null
  } = {}): this {
    return BusinessBrevoTemplate.create({
      businessRef,
      templateIdHu,
      templateIdRo,
      scheduledHour,
    }) as this;
  }

  isSame(other: BusinessBrevoTemplate): boolean {
    if(other === this) {
      return true;
    }

    return isSameData(other.businessRef, this.businessRef) &&
      other.templateIdHu === this.templateIdHu &&
      other.templateIdRo === this.templateIdRo &&
      other.scheduledHour === this.scheduledHour;
  }
}

export class BusinessBrevoTemplateBuilder extends DataModelBuilder<BusinessBrevoTemplate> {
  build(context: SerializationContext, map: SerializedData): BusinessBrevoTemplate {
    return BusinessBrevoTemplate.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(BusinessBrevoTemplate.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(BusinessBrevoTemplate.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version: number = versionRange.to;

    // Renamed field templateId to templateIdHu and added templateIdRo field
    if(version < 2) {
      const templateId = MapUtils.getInteger(map, 'templateId');

      MapUtils.setIfNullOrUndefined(map, 'templateIdHu', templateId);
      MapUtils.setIfNullOrUndefined(map, 'templateIdRo', templateId);

      delete map['templateId'];

      version = 2;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
